import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useSearch } from "@tanstack/react-router";
import { Tag } from "antd";
import {
    useGetIncomingPostbackLogsExportMutation,
    useGetIncomingPostbackLogsQuery,
} from "api/TenantSlug/IncomingPostbackLog/hooks";
import { IncomingPostbackLogDetailDrawer } from "components/Drawers/IncomingPostbackLogDetailDrawer/IncomingPostbackLogDetailDrawer";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { StepLogMessageCell } from "components/UI/Table/UI/StepLogMessageCell/StepLogMessageCell";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useRowHandlers } from "hooks/useTableRowHandlers";
import { Key } from "react";
import { useRecoilValue } from "recoil";
import { IncomingPostbackAction, PermissionEnum, PostbackLogOutput } from "types/enums";
import { DrawerId, TableId } from "types/models";

import GetIncomingPostbackLogsResponse = App.Http.Responses.IncomingPostbackLog.GetIncomingPostbackLogsResponse;
import IncomingPostbackLogData = App.Data.IncomingPostbackLogData;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

interface SearchParams {
    [`incomingPostbackLogDetail-id`]: string;
}

const DEFAULT_LOG_INCLUDES =
    "conversionState,conversionState.owner,conversionState.advertiser,conversionState.offer,incomingPostback";

export const IncomingPostbackLogTable = () => {
    const tableId = TableId.IncomingPostbackLogTable;
    const { t } = usePaldockTranslation("tables.incoming-postback-log");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const { t: tGeneral } = usePaldockTranslation("general");
    const tableAtomInstance = tableAtom<IncomingPostbackLogData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const exportState = useRecoilValue(exportAtom(tableId));
    const permissionsUtils = usePermission();
    const createdAtStr = useCreatedAtFilterValue();
    const searchParams = useSearch({ strict: false }) as SearchParams;

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                ...(tableState.filter?.filter ?? {}),
                ...(tableState.filter?.filter?.created_at
                    ? {}
                    : {
                          created_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            include: tableState.filter?.include ?? DEFAULT_LOG_INCLUDES,
            sort: tableState.filter?.sort ?? "-created_at",
        },
    };

    const getIncomingPostbackLogsQuery = useGetIncomingPostbackLogsQuery(request, { enabled: false });
    const getIncomingPostbackLogsExportMutation = useGetIncomingPostbackLogsExportMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetIncomingPostbackLogsResponse,
        void,
        IncomingPostbackLogData,
        BaseExportRequest
    >(tableId, getIncomingPostbackLogsQuery, undefined, undefined, getIncomingPostbackLogsExportMutation);

    const { getRowHandlers, getRowClassName } = useRowHandlers<IncomingPostbackLogData>({
        actions: [
            {
                type: "drawer",
                drawerId: DrawerId.IncomingPostbackLogDetail,
                drawerParam: `${DrawerId.IncomingPostbackLogDetail}-id`,
                permission: PermissionEnum.IncomingPostbackLogView,
            },
        ],
    });

    const columnConfig: TableColumnType<IncomingPostbackLogData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("date")} />,
            dataIndex: "created_at",
            key: "created_at",
            render: (v) => <DateTimeCell value={v} />,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("origin_id")} />,
            dataIndex: "conversionState.origin_eid",
            key: "conversionState.origin_eid",
            render: (_, row) =>
                row.conversionState?.origin_eid ? (
                    <ShortenedIdWithCopy id={row.conversionState.origin_eid} />
                ) : (
                    <EmptyCell />
                ),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "conversionState.origin_eid",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("event_id")} />,
            dataIndex: "conversionState.eid",
            key: "conversionState.eid",
            render: (_, row) =>
                row.conversionState?.eid ? <ShortenedIdWithCopy id={row.conversionState.eid} /> : <EmptyCell />,
            filterable: true,
            filterType: FILTER_TYPES.eid,
            sorter: false,
            filterName: "conversionState.eid",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("external_id")} />,
            dataIndex: "advertiser_custom_id",
            key: "advertiser_custom_id",
            render: (v) => (v ? <ShortenedIdWithCopy id={v} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "advertiser_custom_id",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("action")} />,
            dataIndex: "action",
            key: "action",
            render: (value) => (value ? tGeneral(`incoming_postback_action.${value}`) : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterOptions: [
                { label: tGeneral("incoming_postback_action.create"), value: IncomingPostbackAction.Create },
                { label: tGeneral("incoming_postback_action.update"), value: IncomingPostbackAction.Update },
            ],
            filterName: "action",
            ellipsis: true,
            width: 120,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("source")} />,
            dataIndex: "source",
            key: "source",
            render: (value) => value ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "source",
            ellipsis: true,
            width: 160,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("transaction")} />,
            dataIndex: "conversionState.type",
            key: "conversionState.type",
            render: (_, row) => row.conversionState?.type ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.conversionStateTypeSelect,
            filterName: "conversionState.type",
            ellipsis: true,
            width: 120,
        },
        TableColumn.Entity.Owner<IncomingPostbackLogData>({
            title: <ColumnHeaderTitle columnTitle={tG("affiliate")} />,
            dataIndex: "conversionState.owner.nickname",
            key: "conversionState.owner.nickname",
            filterName: "conversionState.owner_id",
            sortName: "conversionState.owner.nickname",
            ellipsis: true,
            width: 150,
            getEntity: (row) => row.conversionState?.owner,
        }),
        TableColumn.Entity.Advertiser<IncomingPostbackLogData>({
            title: <ColumnHeaderTitle columnTitle={tG("advertiser")} />,
            dataIndex: "conversionState.advertiser.nickname",
            key: "conversionState.advertiser.nickname",
            filterType: FILTER_TYPES.advertiserSelect,
            filterName: "conversionState.advertiser_id",
            sortName: "conversionState.advertiser.nickname",
            ellipsis: true,
            width: 150,
            getEntity: (row) => row.conversionState?.advertiser,
        }),
        TableColumn.Entity.Offer<IncomingPostbackLogData>({
            title: <ColumnHeaderTitle columnTitle={tG("offer")} />,
            dataIndex: "conversionState.offer.title",
            key: "conversionState.offer.title",
            sortName: "conversionState.offer.title",
            filterName: "conversionState.offer_id",
            ellipsis: true,
            width: 150,
            getEntity: (row) => row.conversionState?.offer,
        }),
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "incomingPostback.name",
            key: "incomingPostback.name",
            render: (_, row) => <EntityLinkCell.IncomingPostback entity={row.incomingPostback} />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "incomingPostback.name",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("state")} />,
            dataIndex: "state",
            key: "state",
            render: (_, row) => {
                const isOk = row.state === true;
                return (
                    <Tag icon={isOk ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={isOk ? "green" : "red"}>
                        {isOk ? tGL("ok") : tGL("fail")}
                    </Tag>
                );
            },
            sorter: true,
            filterable: true,
            filterType: { ...FILTER_TYPES.boolean, booleanLabels: { true: tGL("ok"), false: tGL("fail") } },
            filterName: "state",
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.result")} />,
            dataIndex: "output",
            key: "output",
            render: (v: string) => (v ? tGeneral(`postback_log_output.${v}`) : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "output",
            filterOptions: Object.values(PostbackLogOutput).map((value) => ({
                label: tGeneral(`postback_log_output.${value}`),
                value,
            })),
            ellipsis: true,
            width: 140,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.detail")} />,
            dataIndex: "message",
            key: "message",
            render: (_, row) => <StepLogMessageCell message={row.message} id={`message-${row.id}`} />,
            width: 390,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("commission")} />,
            dataIndex: "data.commission",
            key: "data.commission",
            render: (_, row) => (row.data as any)?.commission ?? <EmptyCell />,
            ellipsis: true,
            width: 120,
            visible: false,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("currency")} />,
            dataIndex: "data.currency",
            key: "data.currency",
            render: (_, row) => (row.data as any)?.currency ?? <EmptyCell />,
            ellipsis: true,
            width: 100,
            visible: false,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("value")} />,
            dataIndex: "data.value",
            key: "data.value",
            render: (_, row) => (row.data as any)?.value ?? <EmptyCell />,
            ellipsis: true,
            width: 100,
            visible: false,
        },
        ...([1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const).map((n) => ({
            title: <ColumnHeaderTitle columnTitle={tG(`advs${n}`)} />,
            dataIndex: `data.advs${n}`,
            key: `data.advs${n}`,
            render: (_: unknown, row: IncomingPostbackLogData) => (row.data as any)?.[`advs${n}`] ?? <EmptyCell />,
            ellipsis: true,
            width: 100,
            visible: false,
        })),
    ];

    return (
        <>
            <Table<GetIncomingPostbackLogsResponse, IncomingPostbackLogData>
                tableId={tableId}
                columnConfig={columnConfig}
                data={tableFetchDataUtils.tableData as GetIncomingPostbackLogsResponse}
                dataSource={tableFetchDataUtils.tableData?.incomingPostbackLogs ?? []}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.id as Key}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                loading={tableFetchDataUtils.fetchQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                enableFilters={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilter)}
                enableColumnsVisibility={permissionsUtils.hasPermission(PermissionEnum.LogColumnSetting)}
                enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilterTime)}
                dateFilterDataIndex={"created_at"}
                dateFilterMode={"minute"}
                autoScrollWidth={true}
                hideActionsColumn={true}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
            />

            {permissionsUtils.hasPermission(PermissionEnum.IncomingPostbackLogView) &&
                searchParams[`${DrawerId.IncomingPostbackLogDetail}-id`] && (
                    <IncomingPostbackLogDetailDrawer logId={searchParams[`${DrawerId.IncomingPostbackLogDetail}-id`]} />
                )}
        </>
    );
};
